import * as RxToggleGroup from '@radix-ui/react-toggle-group';
import type { ReactNode } from 'react';

import Text from '../base/Text';

type ToggleGroupItem = {
  label: string;
  value: string | number;
  content: ReactNode;
};

type ToggleGroupVariant = 'default' | 'compact';
type ToggleGroupSize = 'default' | 'sm';

interface ToggleGroupBaseProps {
  items: ToggleGroupItem[];
  label?: string;
  id?: string;
  stretch?: boolean;
  className?: string;
  variant?: ToggleGroupVariant;
  size?: ToggleGroupSize;
}

type ToggleGroupProps =
  | (ToggleGroupBaseProps & {
      type?: 'single';
      value?: string | number;
      onValueChange?: (value: string) => void;
    })
  | (ToggleGroupBaseProps & {
      type: 'multiple';
      value?: (string | number)[];
      onValueChange?: (value: string[]) => void;
    });

export default function ToggleGroup(props: ToggleGroupProps) {
  const {
    items,
    label,
    id,
    stretch,
    className,
    variant = 'default',
    size = 'default',
  } = props;
  const isCompact = variant === 'compact';
  const isSmall = size === 'sm';
  const rootClassName = `flex ${isCompact ? 'max-w-full flex-nowrap gap-0' : 'gap-2'} ${
    stretch ? 'w-full' : ''
  } ${className ?? ''}`;
  const toggleItems = items.map((item, index) => {
    const radiusClassName = isCompact
      ? `${index === 0 ? 'rounded-l-lg' : 'rounded-l-none'} ${
          index === items.length - 1 ? 'rounded-r-lg' : 'rounded-r-none'
        }`
      : 'rounded-lg';

    return (
      <RxToggleGroup.Item
        key={item.value}
        className={`bg-gray-30 dark:bg-gray-60 dark:data-[state=off]:text-gray-60 dark:data-[state=off]:bg-gray-75 text-gray-90 data-[state=off]:bg-gray-15 data-[state=off]:text-gray-45 hover:data-[state=off]:bg-gray-30 dark:hover:data-[state=off]:bg-gray-60 flex ${
          isSmall ? 'h-8' : 'h-10'
        } cursor-pointer items-center justify-center text-center dark:text-white dark:hover:data-[state=off]:text-gray-50 ${radiusClassName} ${
          isCompact
            ? 'min-w-0 overflow-hidden px-3'
            : `min-w-[40px] ${isSmall ? 'px-3' : 'px-5'}`
        } ${stretch ? 'flex-1' : ''}`}
        value={item.value.toString()}
        aria-label={item.label}
      >
        <Text
          variant="studio/body/medium/sm"
          className={isCompact ? 'block max-w-full truncate' : undefined}
        >
          {item.content}
        </Text>
      </RxToggleGroup.Item>
    );
  });

  if (props.type === 'multiple') {
    return (
      <RxToggleGroup.Root
        id={id}
        className={rootClassName}
        type="multiple"
        aria-label={label}
        value={props.value?.map((itemValue) => itemValue.toString())}
        onValueChange={(value) => {
          // This prevents deselecting the final active button.
          if (value.length > 0) {
            props.onValueChange?.(value);
          }
        }}
      >
        {toggleItems}
      </RxToggleGroup.Root>
    );
  }

  return (
    <RxToggleGroup.Root
      id={id}
      className={rootClassName}
      type="single"
      aria-label={label}
      value={props.value?.toString()}
      onValueChange={(value) => {
        // This prevents deselecting the currently active button
        if (value !== '') {
          props.onValueChange?.(value);
        }
      }}
    >
      {toggleItems}
    </RxToggleGroup.Root>
  );
}
